import { format } from 'date-fns';
import {
  LS_KEY_FREE_UNLOCK_OFFER_INTENT,
  LS_KEY_FREE_UNLOCK_OFFER_PROMPTED,
} from 'src/constants/localStorageKeys';
import type { FreeUnlockPromo } from 'src/redux/types/employerCredit';

/**
 * Free-unlock offers are invite-only. An org is invited when it's in an offer's
 * audience (`promo.invited`, decided server-side), or when one of its members
 * opens the offer's link, `…/jobs?free-credit-unlock-offer=<offer id>`. This
 * module is the memory of "this browser arrived through an offer link": the
 * employer layout hands the id to the server once the member is signed in
 * (claimFreeUnlockOfferLink), which puts the org on the offer's list, so from
 * then on the popup opens for the whole team on every device.
 *
 * It has to be localStorage rather than the URL, because every route into the
 * employer dashboard drops the query string somewhere along the way:
 * EmployerLayout `navigate('/employer-post')` for a fresh signup, the
 * logged-out `location.replace(getPublicRedirectPathname(...))` bounce, the
 * Google OAuth round-trip, and the landing-page → login handoff. And it can't
 * reuse LS_KEY_ON_AUTH_REDIRECT_PATHNAME: that key is a single slot consumed by
 * the first `resolvePostAuthRoute` after sign-in and cleared on logout, so the
 * offer would be swallowed by — or would swallow — an ordinary return path.
 */
export const FREE_UNLOCK_OFFER_PARAM = 'free-credit-unlock-offer';

const OFFER_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** An offer's invite link. Kept here so the admin panel and emails can't drift. */
export const buildFreeUnlockOfferUrl = (baseUrl: string, offerId: string) =>
  `${baseUrl.replace(/\/$/, '')}/jobs?${FREE_UNLOCK_OFFER_PARAM}=${offerId}`;

/** Called once at boot, before React mounts, so the id is kept even on a route
 * that immediately redirects away. The old `=true` links named no offer, so
 * they're ignored. */
export const captureFreeUnlockOfferIntent = (): void => {
  try {
    const param = new URLSearchParams(window.location.search).get(
      FREE_UNLOCK_OFFER_PARAM,
    );
    if (param && OFFER_ID.test(param)) {
      localStorage.setItem(LS_KEY_FREE_UNLOCK_OFFER_INTENT, param);
    }
  } catch {
    // localStorage unavailable (private mode, quota) — the offer is best-effort
  }
};

/** The offer id this browser arrived with, if any. */
export const getFreeUnlockOfferIntent = (): string | null => {
  try {
    const value = localStorage.getItem(LS_KEY_FREE_UNLOCK_OFFER_INTENT);
    return value && OFFER_ID.test(value) ? value : null;
  } catch {
    return null;
  }
};

export const hasFreeUnlockOfferIntent = (): boolean =>
  !!getFreeUnlockOfferIntent();

/** Whether to offer this employer the activation popup at all. */
export const isInvitedToFreeUnlockOffer = (
  promo: FreeUnlockPromo | null,
): boolean => !!promo?.invited;

/** Dropped once the link has been handed to the server (or turned out to be
 * for an offer that's over) — the server remembers the invite from then on. */
export const clearFreeUnlockOfferIntent = (): void => {
  try {
    localStorage.removeItem(LS_KEY_FREE_UNLOCK_OFFER_INTENT);
  } catch {
    // ignore
  }
};

/**
 * The popup auto-opens at most once a day for each org and offer, in this
 * browser. Once per tab session meant a fresh popup in every new tab, which
 * nags an employer who works across tabs; the dashboard banner keeps a way in
 * between days. Stored as {"<orgId>:<offerId>": "YYYY-MM-DD"}.
 */
const promptKey = (orgId: string, offerId: string) => `${orgId}:${offerId}`;
// The employer's own calendar day, not UTC's (which rolls over at 05:30 IST).
const todayKey = () => format(new Date(), 'yyyy-MM-dd');

const readPrompted = (): Record<string, string> => {
  try {
    return JSON.parse(
      localStorage.getItem(LS_KEY_FREE_UNLOCK_OFFER_PROMPTED) || '{}',
    );
  } catch {
    return {};
  }
};

export const wasFreeUnlockOfferPromptedToday = (
  orgId: string,
  offerId: string,
): boolean => readPrompted()[promptKey(orgId, offerId)] === todayKey();

export const markFreeUnlockOfferPromptedToday = (
  orgId: string,
  offerId: string,
): void => {
  try {
    const today = todayKey();
    // Only today's entries are worth keeping, so the map never grows.
    const kept = Object.fromEntries(
      Object.entries(readPrompted()).filter(([, day]) => day === today),
    );
    localStorage.setItem(
      LS_KEY_FREE_UNLOCK_OFFER_PROMPTED,
      JSON.stringify({ ...kept, [promptKey(orgId, offerId)]: today }),
    );
  } catch {
    // ignore
  }
};
